import { apiGenerateImageFromHtml } from '../api/adcritterApi'
import { extractHtmlWithStyles } from './htmlExtractor'

export interface FullPageScreenshotOptions {
  tabId: string
  width: number
  height: number
  timeout: number
  quality: number
  containerSelector: string
}

export interface FullPageScreenshotResult {
  success: boolean
  imageData?: string
  error?: string
  tabId: string
}
export const captureFullPageScreenshotWithFallback = async (options: FullPageScreenshotOptions): Promise<FullPageScreenshotResult> => {
  const { tabId, width, height, quality, timeout, containerSelector } = options

  try {
    const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error(`Screenshot capture timeout after ${timeout}ms`)), timeout))

    const screenshotPromise = captureScreenshot({
      tabId,
      width,
      height,
      quality,
      containerSelector
    })

    const imageData = await Promise.race([screenshotPromise, timeoutPromise])

    return {
      success: true,
      imageData,
      tabId
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)

    console.error(`Full-page screenshot capture failed for tab ${tabId}:`, {
      error: errorMessage,
      tabId,
      options
    })

    return {
      success: false,
      error: errorMessage,
      tabId
    }
  }
}

const captureScreenshot = async (options: { tabId: string; width: number; height: number; quality: number; containerSelector: string }): Promise<string> => {
  const { tabId, width, height, quality, containerSelector } = options

  const containerElement = document.querySelector(containerSelector) as HTMLElement
  if (!containerElement) {
    throw new Error(`Container element not found: ${containerSelector}`)
  }

  const htmlContent = await extractHtmlWithStyles(containerElement, {
    pageTitle: `Full Page Screenshot - ${tabId}`,
    includeStyles: true,
    sanitize: true,
    hideModals: true
  })

  const fileName = `${tabId}_screenshot.png`
  const format = 'png' as const

  const imageBlob = await apiGenerateImageFromHtml(fileName, htmlContent, format, width, height, quality, false)

  const imageDataUrl = URL.createObjectURL(imageBlob)

  return imageDataUrl
}
